import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AnimationOptions } from 'ngx-lottie';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { applyLottieThemeToJson, LottieResolvedThemeColors } from './apply-lottie-theme-to-json';
import { parseCssColorToLottieRgba } from './parse-css-color-to-lottie';

/**
 * NIF-751: Lottie = grafismos animados.
 * Prioridad: tokens Infinity de grafismos; fallback bridges --pwcs-icon-* / legacy lottie.
 */
const CSS_VAR_SURFACE_INVERSE = '--surface-inverse';
const CSS_VAR_ICON_HIGHLIGHT = '--icon-highlight';
const CSS_VAR_SURFACE_BRAND_PRIMARY = '--surface-brand-primary';
/** Borde secundario grafismos / óvalo Lottie (Carmen NIF-751). */
const CSS_VAR_SURFACE_BRAND_SECONDARY = '--surface-brand-secondary';
const CSS_VAR_ICON_BORDER = '--pwcs-icon-border';
const CSS_VAR_ICON_BACKGROUND = '--pwcs-icon-background';
const CSS_VAR_ICON_SECONDARY_BORDER = '--pwcs-icon-secondary-border';
const CSS_VAR_LOTTIE_BORDER = '--pwcs-lottie-frame-border';
const CSS_VAR_LOTTIE_ICON = '--pwcs-lottie-frame-icon-border';
const CSS_VAR_LOTTIE_BG = '--pwcs-lottie-frame-bg';

@Injectable({
	providedIn: 'root',
})
export class LottieThemeService {
	private readonly http = inject(HttpClient);

	/**
	 * Carga JSON, aplica colores desde variables CSS del diseño y devuelve opciones para ng-lottie.
	 */
	public loadThemedAnimation(assetPath: string | null): Observable<AnimationOptions | null> {
		if (!assetPath) {
			return of(null);
		}
		return this.http.get<unknown>(assetPath).pipe(
			map((data) => {
				const theme = this.resolveThemeColorsFromDocument();
				if (!this.hasAnyThemeOverride(theme)) {
					// Sin colores desde back: mantener comportamiento original de Lottie.
					return { path: assetPath } as AnimationOptions;
				}
				const clone =
					typeof structuredClone === 'function'
						? structuredClone(data)
						: (JSON.parse(JSON.stringify(data)) as unknown);
				applyLottieThemeToJson(clone, theme);
				return { animationData: clone } as AnimationOptions;
			}),
			catchError(() => of({ path: assetPath } as AnimationOptions)),
		);
	}

	private resolveThemeColorsFromDocument(): LottieResolvedThemeColors {
		return {
			borderRgb:
				this.readCssColor(CSS_VAR_SURFACE_INVERSE) ??
				this.readCssColor(CSS_VAR_ICON_BORDER) ??
				this.readCssColor(CSS_VAR_LOTTIE_BORDER),
			iconBorderRgb:
				this.readCssColor(CSS_VAR_ICON_HIGHLIGHT) ?? this.readCssColor(CSS_VAR_LOTTIE_ICON),
			backgroundRgb:
				this.readCssColor(CSS_VAR_SURFACE_BRAND_PRIMARY) ??
				this.readCssColor(CSS_VAR_ICON_BACKGROUND) ??
				this.readCssColor(CSS_VAR_LOTTIE_BG),
			// Óvalo: bridge / override en body (css_config). No usar getComputedStyle de
			// --surface-brand-secondary (#f3effc = superficie DS, no borde grafismos).
			// Sin css_config → --icon-highlight (= default admin icon_secondary_border).
			secondaryBorderRgb:
				this.readCssColor(CSS_VAR_ICON_SECONDARY_BORDER) ??
				this.readInlineCssColor(CSS_VAR_SURFACE_BRAND_SECONDARY) ??
				this.readCssColor(CSS_VAR_ICON_HIGHLIGHT),
		};
	}

	private hasAnyThemeOverride(theme: LottieResolvedThemeColors): boolean {
		return Boolean(
			theme.borderRgb || theme.iconBorderRgb || theme.backgroundRgb || theme.secondaryBorderRgb,
		);
	}

	/** Solo `body.style` (overrides DesignService / css_config). */
	private readInlineCssColor(varName: string): [number, number, number] | null {
		try {
			if (typeof document === 'undefined') {
				return null;
			}
			const raw = document.body.style.getPropertyValue(varName).trim();
			if (!raw) {
				return null;
			}
			const parsed = parseCssColorToLottieRgba(raw);
			if (parsed && parsed[3] >= 0.01) {
				return [parsed[0], parsed[1], parsed[2]];
			}
		} catch {
			// Ignorar errores de parseo DOM
		}
		return null;
	}

	private readCssColor(varName: string): [number, number, number] | null {
		try {
			if (typeof document === 'undefined') {
				return null;
			}
			const raw = document.body.style.getPropertyValue(varName).trim();
			const fromDom = raw !== '' ? raw : getComputedStyleSafe(varName);
			if (!fromDom) {
				return null;
			}
			const parsed = parseCssColorToLottieRgba(fromDom);
			if (parsed && parsed[3] >= 0.01) {
				return [parsed[0], parsed[1], parsed[2]];
			}
		} catch {
			// Ignorar errores de parseo DOM
		}
		return null;
	}
}

function getComputedStyleSafe(varName: string): string {
	try {
		return getComputedStyle(document.body).getPropertyValue(varName).trim();
	} catch {
		return '';
	}
}
